import type { EventElement, Events, ListenerOptions } from './types';

/**
 * Adds multiple event listeners of the same event type to a given element or window.
 *
 * @typeParam E - The event type, extending one of the values from the `Events` interface.
 * @param element - The target element or window to which the event listeners will be attached.
 * @param handler - The event handler function to be called when the event is triggered.
 * @param options - Optional. Specifies characteristics about the event listener, such as whether
 * it is passive or should capture events.
 * @param eventNames - The names of the events to listen for, filtered to only those compatible with
 * the handler's event type.
 *
 * @example
 * ```typescript
 * addEventListeners(window, (e: KeyboardEvent) => { ... }, false, 'keydown', 'keyup');
 * ```
 */
export function addEventListeners<E extends Events[keyof Events]>(
  element: EventElement | Window,
  handler: (event: E) => void,
  options: ListenerOptions = false,
  ...eventNames: (keyof Pick<Events, { [K in keyof Events]: Events[K] extends E ? K : never }[keyof Events]>)[]
) {
  for (const eventName of eventNames) {
    element.addEventListener(eventName, handler as EventListener, options);
  }
}
